/**
 * rag-ingestion-service.ts
 *
 * Scoped RAG & WikiLLM Document Ingestion Engine with multi-tier partitioning,
 * ephemeral privacy sanitization, and tombstone re-indexing guards.
 */

export type EmbeddingScope = 'global' | 'plugin' | 'channel';
export type DocumentSensitivity = 'public' | 'internal' | 'confidential' | 'restricted';
export type IngestionStatus = 'pending' | 'scanning' | 'indexed' | 'quarantined' | 'retracted';

export interface KnowledgeDocument {
  readonly documentId: string;
  readonly scope: EmbeddingScope;
  readonly scopeId: string; // e.g. "global", "plugin:scrum", "channel:confidential-01"
  readonly tenantId: string;
  readonly title: string;
  readonly content: string;
  readonly sourceUri?: string;
  readonly contentHash: string;
  readonly sensitivity: DocumentSensitivity;
  readonly status: IngestionStatus;
  readonly quarantinedReason?: string;
  readonly indexedAt?: number;
  readonly chunks?: readonly DocumentChunk[];
}

export interface DocumentChunk {
  readonly chunkId: string;
  readonly documentId: string;
  readonly scopeId: string;
  readonly tenantId: string;
  readonly chunkIndex: number;
  readonly text: string;
  readonly tokenCount: number;
  readonly hash: string;
  readonly embedding?: readonly number[];
}

export interface RetrievalQueryResult {
  readonly chunks: readonly {
    readonly chunkId: string;
    readonly documentId: string;
    readonly title: string;
    readonly text: string;
    readonly score: number;
    readonly scopeId: string;
  }[];
  readonly authorizedCollections: readonly string[];
  readonly rejectedCandidateCount: number;
}

export class RagIngestionService {
  private static instance: RagIngestionService | null = null;
  private documents: Map<string, KnowledgeDocument> = new Map();
  private chunks: Map<string, DocumentChunk> = new Map();
  private tombstones: Set<string> = new Set(); // Document IDs marked with tombstone

  public static getInstance(): RagIngestionService {
    if (!RagIngestionService.instance) {
      RagIngestionService.instance = new RagIngestionService();
    }
    return RagIngestionService.instance;
  }

  public clear(): void {
    this.documents.clear();
    this.chunks.clear();
    this.tombstones.clear();
  }

  /**
   * Compute a simple deterministic content hash (SHA-256 equivalent for memory)
   */
  public computeHash(content: string): string {
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      hash = (hash << 5) - hash + content.charCodeAt(i);
      hash |= 0;
    }
    return `hash-${Math.abs(hash).toString(16)}`;
  }

  /**
   * Ephemeral privacy sanitization: scrubs API keys, bearer tokens, and prompt injection patterns.
   */
  public sanitizeContent(raw: string): { cleanText: string; isMalicious: boolean; reason?: string } {
    let text = raw;

    // Check for prompt injection / system override attempts
    const injectionPatterns = [
      /ignore previous instructions/i,
      /system prompt override/i,
      /disregard all guardrails/i,
    ];
    for (const pattern of injectionPatterns) {
      if (pattern.test(text)) {
        return { cleanText: '', isMalicious: true, reason: 'prompt_injection_detected' };
      }
    }

    // Scrub API keys & bearer tokens
    text = text.replace(/sk-[a-zA-Z0-9_-]{20,}/g, '[REDACTED_API_KEY]');
    text = text.replace(/bearer\s+[a-zA-Z0-9_.-]{20,}/gi, 'Bearer [REDACTED_TOKEN]');
    text = text.replace(/ghp_[a-zA-Z0-9]{30,}/g, '[REDACTED_GITHUB_KEY]');

    return { cleanText: text, isMalicious: false };
  }

  /**
   * Splits sanitized text into ~500-token semantic chunks with 50-token overlap.
   */
  public chunkText(text: string, chunkSize: number = 500, overlap: number = 50): string[] {
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length <= chunkSize) {
      return [words.join(' ')];
    }

    const result: string[] = [];
    let start = 0;
    while (start < words.length) {
      const end = Math.min(start + chunkSize, words.length);
      result.push(words.slice(start, end).join(' '));
      if (end >= words.length) break;
      start += chunkSize - overlap;
    }
    return result;
  }

  /**
   * Ingests a new document with full validation, sanitization, chunking, and duplicate detection.
   */
  public async ingestDocument(doc: {
    documentId: string;
    scope: EmbeddingScope;
    scopeId: string;
    tenantId: string;
    title: string;
    content: string;
    sensitivity?: DocumentSensitivity;
  }): Promise<{ ok: boolean; documentId: string; status: IngestionStatus; chunkCount: number; error?: string }> {
    // Check if document was tombstoned
    if (this.tombstones.has(doc.documentId)) {
      return { ok: false, documentId: doc.documentId, status: 'retracted', chunkCount: 0, error: 'document_tombstoned' };
    }

    const contentHash = this.computeHash(doc.content);

    // Duplicate check within same scope
    for (const existing of this.documents.values()) {
      if (existing.scopeId === doc.scopeId && existing.contentHash === contentHash && existing.status === 'indexed') {
        return { ok: false, documentId: doc.documentId, status: 'quarantined', chunkCount: 0, error: 'duplicate_content_detected' };
      }
    }

    // Ephemeral sanitization
    const { cleanText, isMalicious, reason } = this.sanitizeContent(doc.content);
    if (isMalicious) {
      const quarantinedDoc: KnowledgeDocument = {
        documentId: doc.documentId,
        scope: doc.scope,
        scopeId: doc.scopeId,
        tenantId: doc.tenantId,
        title: doc.title,
        content: doc.content,
        contentHash,
        sensitivity: doc.sensitivity || 'restricted',
        status: 'quarantined',
        quarantinedReason: reason,
      };
      this.documents.set(doc.documentId, quarantinedDoc);
      return { ok: false, documentId: doc.documentId, status: 'quarantined', chunkCount: 0, error: reason };
    }

    // Chunking
    const rawChunks = this.chunkText(cleanText);
    const documentChunks: DocumentChunk[] = rawChunks.map((chunkText, idx) => ({
      chunkId: `${doc.documentId}-chunk-${idx}`,
      documentId: doc.documentId,
      scopeId: doc.scopeId,
      tenantId: doc.tenantId,
      chunkIndex: idx,
      text: chunkText,
      tokenCount: chunkText.split(/\s+/).length,
      hash: this.computeHash(chunkText),
    }));

    const indexedDoc: KnowledgeDocument = {
      documentId: doc.documentId,
      scope: doc.scope,
      scopeId: doc.scopeId,
      tenantId: doc.tenantId,
      title: doc.title,
      content: cleanText,
      contentHash,
      sensitivity: doc.sensitivity || 'internal',
      status: 'indexed',
      indexedAt: Date.now(),
      chunks: documentChunks,
    };

    this.documents.set(doc.documentId, indexedDoc);
    documentChunks.forEach((c) => this.chunks.set(c.chunkId, c));

    return { ok: true, documentId: doc.documentId, status: 'indexed', chunkCount: documentChunks.length };
  }

  /**
   * Marks a document with a Tombstone. Instantly retracts chunks from active search index.
   */
  public tombstoneDocument(documentId: string): void {
    this.tombstones.add(documentId);
    const existing = this.documents.get(documentId);
    if (existing) {
      this.documents.set(documentId, { ...existing, status: 'retracted' });
      existing.chunks?.forEach((c) => this.chunks.delete(c.chunkId));
    }
  }

  /**
   * Re-indexing Invariant: Rebuilds vector & keyword indices from scratch, strictly excluding
   * tombstones and quarantined documents.
   */
  public rebuildIndices(): { reindexedCount: number; excludedTombstones: number; excludedQuarantined: number } {
    let reindexed = 0;
    let excludedTombstones = 0;
    let excludedQuarantined = 0;

    this.chunks.clear();

    for (const [docId, doc] of this.documents.entries()) {
      if (this.tombstones.has(docId) || doc.status === 'retracted') {
        excludedTombstones++;
        continue;
      }
      if (doc.status === 'quarantined') {
        excludedQuarantined++;
        continue;
      }
      if (doc.status === 'indexed' && doc.chunks) {
        doc.chunks.forEach((c) => this.chunks.set(c.chunkId, c));
        reindexed++;
      }
    }

    return { reindexedCount: reindexed, excludedTombstones, excludedQuarantined };
  }

  /**
   * Scoped Hybrid Retrieval: Enforces caller authorization over scope partitions.
   * A caller requesting search in `scopeId` only receives chunks from authorized scopes.
   */
  public queryKnowledge(params: {
    query: string;
    tenantId: string;
    authorizedScopeIds: readonly string[];
    topK?: number;
  }): RetrievalQueryResult {
    const { query, tenantId, authorizedScopeIds, topK = 5 } = params;
    const qWords = query.toLowerCase().split(/\s+/).filter(Boolean);

    const candidates: { chunk: DocumentChunk; doc: KnowledgeDocument; score: number }[] = [];
    let rejectedCount = 0;

    for (const chunk of this.chunks.values()) {
      if (chunk.tenantId !== tenantId) continue;

      // Strict Scope Access Gate
      if (!authorizedScopeIds.includes(chunk.scopeId)) {
        rejectedCount++;
        continue;
      }

      const doc = this.documents.get(chunk.documentId);
      if (!doc || doc.status !== 'indexed') continue;

      // Lexical BM25 approximation
      let matches = 0;
      const lower = chunk.text.toLowerCase();
      for (const w of qWords) {
        if (lower.includes(w)) matches++;
      }

      if (matches > 0) {
        const score = matches / Math.max(1, qWords.length);
        candidates.push({ chunk, doc, score });
      }
    }

    candidates.sort((a, b) => b.score - a.score);
    const top = candidates.slice(0, topK);

    return {
      chunks: top.map((c) => ({
        chunkId: c.chunk.chunkId,
        documentId: c.doc.documentId,
        title: c.doc.title,
        text: c.chunk.text,
        score: c.score,
        scopeId: c.chunk.scopeId,
      })),
      authorizedCollections: authorizedScopeIds,
      rejectedCandidateCount: rejectedCount,
    };
  }
}

export const globalRagIngestionService = RagIngestionService.getInstance();
